import { useTranslation } from 'react-i18next'
// eslint-disable-next-line lodash/import-scope
import _ from 'lodash'
import { useSelector } from 'react-redux'

import { TabInfo } from '../../../components/Tabs/Tabs'
import { Reports } from '../../../containers/CompanyDetail/Reports'
import { Company } from '../../../redux/companyDetail/types'
import { useDateFormat } from '../../../hooks/useDateFormat'
import { useDomains } from '../../../hooks/useDomains'
import { DomainType } from '../../../redux/domains'
import { CompanyDetailReportsProps } from '../../../containers/CompanyDetail/Reports/types'
import { getDomesticOfferingCountries, getDomesticOfferingFallbackCountry } from '../../../redux/config'

import { defaultDeliveryOptionLabelRenderer } from '../../utils/config'

import { useCompanyDetailPageConfig as configVN } from './configVN'
import { useCompanyDetailPageConfig as configMY } from './configMY'
import { useCompanyDetailPageConfig as configID } from './configID'
import { useCompanyDetailPageConfig as configPH } from './configPH'
import { useCompanyDetailPageConfig as configTH } from './configTH'
import { useCompanyDetailPageConfig as configSG } from './configSG'
import { useCompanyDetailPageConfig as configTW } from './configTW'

export const defaultFormatAddress = (company: Company) => {
  const { fullAddress, city } = company.hqAddress
  let address = ''
  if (fullAddress && city) {
    address = `${fullAddress}, ${city}`
  } else if (fullAddress) {
    address = fullAddress
  } else if (city) {
    address = city
  }

  return address
}

export type DisplayField = {
  label: string
  value: React.ReactText | React.ReactText[]
  maxItems?: number
}

export type CompanyDetailFields = {
  authFields: DisplayField[]
  notAuthFields: DisplayField[]
}

export type Tab<C> = {
  component?: React.ComponentType<C>
  componentProps?: Partial<C>
} & TabInfo<number>

export type CompanyDetailPageConfig = {
  generateFields: (x: Company) => CompanyDetailFields
  tabs: [Tab<CompanyDetailReportsProps>, Tab<any>, Tab<any>, Tab<any>]
  displayCompanyDescription?: boolean
  formatAddress: (company: Company) => string
}

export function GenericCompanyDetailConfig(country: string): CompanyDetailPageConfig {
  const { t } = useTranslation('companyDetail')

  const { formatDate } = useDateFormat()

  return {
    generateFields: (c: Company) => {
      const ret: CompanyDetailFields = {
        notAuthFields: _.filter(
          [
            {
              label: t('Address'),
              value:
                c.hqAddress?.street && c.hqAddress?.houseNumber
                  ? `${c.hqAddress?.street} ${c.hqAddress?.houseNumber}`
                  : c.hqAddress?.fullAddress
                    ? c.hqAddress?.fullAddress
                    : ''
            },
            {
              label: t('City'),
              value: c.hqAddress.city ?? ''
            },
            {
              label: t('Country'),
              value: country
            },
            {
              label: t('NPWPCode'),
              value: c.npwpCode ?? ''
            },
            {
              label: t('LegalizationCode'),
              value: c.legalizationCode ?? ''
            }
          ],
          x => !_.isEmpty(x.value)
        ),
        authFields: [
          {
            label: t('Industry'),
            value: c.sic ?? ''
          },
          {
            label: t('LegalForm'),
            value: c.legalForm ?? ''
          },
          {
            label: t('IncorporationDate'),
            value: c.incorporationDate
              ? formatDate(c.incorporationDate, {
                  dateStyle: 'medium'
                })
              : ''
          },
          {
            label: t('LastUpdateDate'),
            value: c.lastUpdateDate
              ? formatDate(c.lastUpdateDate, {
                  dateStyle: 'medium'
                })
              : ''
          }
        ]
      }

      return ret
    },
    tabs: [
      {
        key: 1,
        title: t('Tabs.Reports'),
        component: Reports,
        componentProps: {
          renderDeliveryOptionLabel: defaultDeliveryOptionLabelRenderer
        }
      },
      {
        key: 2,
        title: t('Tabs.Financial')
      },
      {
        key: 3,
        title: t('Tabs.CompanyNetwork')
      },
      {
        key: 4,
        title: t('Tabs.Industry')
      }
    ],
    formatAddress: defaultFormatAddress
  }
}

export function useCompanyDetailConfig(countryCode: string, domainCode: string): CompanyDetailPageConfig {
  const normalizedCountryCode = _.toUpper(countryCode)
  const normalizedDomainCode = _.toUpper(domainCode)

  const { umbracoData } = useDomains([DomainType.Country], domainCode)
  const country = umbracoData ? umbracoData[DomainType.Country][normalizedCountryCode] : ''

  const domesticOfferingCountries = useSelector(getDomesticOfferingCountries)
  const domesticFallbackCountry = useSelector(getDomesticOfferingFallbackCountry)

  const configCountryCode = _.some(domesticOfferingCountries, x => _.toUpper(x) === normalizedCountryCode)
    ? domesticFallbackCountry
    : normalizedCountryCode

  switch (configCountryCode) {
    case 'MY':
      return configMY(country)
    case 'VN':
      return { ...configVN(country), displayCompanyDescription: normalizedDomainCode === 'VN' }
    case 'ID':
      return configID(country)
    case 'PH':
      return configPH(country)
    case 'TH':
      return configTH(country)
    case 'TW':
      return configTW(country)
    case 'SG':
      return configSG()
  }

  return GenericCompanyDetailConfig(country)
}
